import { useEffect, useState } from 'react';
import type { CameraFeature } from '@tsr/shared-types';
import { CameraMedia } from './CameraMedia';
import { announcePopout } from './popout';

/**
 * Multi-monitor pop-out view (Phase V Part 2). Served at /popout#cam=<uuid>.
 *
 * Renders only the camera media + minimal chrome (name, source) — no map, nav,
 * or side panel — so the operator can drag the window to a second monitor. On
 * mount it announces itself on the pop-out BroadcastChannel so the main map can
 * pulse the matching marker; the cleanup / beforeunload announces close.
 */
type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; camera: CameraFeature }
  | { kind: 'notfound' };

function parseCamId(): string | null {
  const m = /[#&]cam=([0-9a-fA-F-]{36})/.exec(window.location.hash);
  return m ? m[1]! : null;
}

export function Popout(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    const id = parseCamId();
    if (!id) {
      setState({ kind: 'notfound' });
      return undefined;
    }
    let cancelled = false;
    void fetch(`/api/cameras/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((feat: CameraFeature | null) => {
        if (cancelled) return;
        if (feat && (feat as { type?: string }).type === 'Feature') {
          setState({ kind: 'ready', camera: feat });
        } else {
          setState({ kind: 'notfound' });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'notfound' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Announce open/close to the main window for marker-pulse sync, once resolved.
  useEffect(() => {
    if (state.kind !== 'ready') return undefined;
    return announcePopout(state.camera.id);
  }, [state]);

  if (state.kind === 'loading') {
    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-950 text-slate-300">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-500 border-t-transparent" />
      </div>
    );
  }

  if (state.kind === 'notfound') {
    return (
      <div
        data-testid="popout-notfound"
        className="flex h-full w-full flex-col items-center justify-center gap-2 bg-slate-950 text-slate-300"
      >
        <p className="text-sm font-medium">Camera not found</p>
        <p className="text-xs text-slate-500">This pop-out link points to a camera that no longer resolves.</p>
      </div>
    );
  }

  const { camera } = state;
  return (
    <div className="flex h-full w-full flex-col bg-slate-950 text-slate-100">
      <header className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
        <div className="min-w-0">
          <h1 className="truncate text-sm font-semibold" data-testid="popout-title">
            {camera.properties.name}
          </h1>
          <p className="truncate text-[11px] text-slate-400">
            {camera.properties.source} · {camera.properties.stream_type}
          </p>
        </div>
      </header>
      <div className="flex flex-1 items-center justify-center p-3">
        <div className="w-full">
          {/* enablePopout=false → the pop-out window doesn't offer to re-pop itself. */}
          <CameraMedia camera={camera} enablePopout={false} />
        </div>
      </div>
    </div>
  );
}
